import { Fragment, useEffect } from "react";
import { Path, useForm } from "react-hook-form";

import MainButton from "~/components/common/mainButton";
import { useLanguage } from "~/contexts/providers/language";
import englishTranslations from "~/localization/en";
import frenchTranslations from "~/localization/fr";
import { MembershipFieldSet } from "~/types/account/account.types";

import styles from "./index.module.scss";

const SetDefaultValues = <T extends object>(data: T, key: string) => {
  if (key in data) {
    const val = data[key as keyof T];
    if (!!val) return val;
  }
  return undefined;
};

type Props<T> = {
  title: string;
  fieldSets: MembershipFieldSet<Path<T>>[];
  errorField?: Path<T>;
  defaultValues?: object;
  isLoading?: boolean;
  submitButtonText?: string;
  handleEmptyErrorField?: () => void;
  onSubmit: (data: T) => void;
  error?: string;
  login?: boolean;
};

const AccountMembershipForm = <T extends object>({
  title,
  fieldSets,
  errorField,
  defaultValues,
  isLoading,
  handleEmptyErrorField,
  onSubmit,
  error,
  login,
}: Props<T>) => {
  const { register, handleSubmit, setFocus } = useForm<T>();

  const { language } = useLanguage();
  const accountTexts =
    language === "En"
      ? englishTranslations.accountPage
      : frenchTranslations.accountPage;

  useEffect(() => {
    if (errorField) {
      setFocus(errorField);
      if (handleEmptyErrorField) handleEmptyErrorField();
    }
  }, [errorField, handleEmptyErrorField, setFocus]);

  return (
    <form
      className={`${styles.accountMembershipForm} ${
        login ? styles.noBorder : ""
      }`}
      onSubmit={(e) => {
        handleSubmit(onSubmit)(e);
      }}
    >
      {!login && <h5 className="small-title">{title}</h5>}
      {fieldSets.map((fieldSet, i) => (
        <fieldset key={`${title}-fieldSet-${i}`}>
          <legend>{fieldSet.legend}</legend>
          {fieldSet.inputs.map((input, j) => (
            <Fragment key={j}>
              <label>{input.label ?? input.placeholder}</label>
              <input
                {...register(input.register)}
                type={input.type ?? "text"}
                placeholder={input.placeholder}
                size={input.size ?? 12}
                disabled={input.disabled}
                readOnly={input.disabled}
                defaultValue={
                  defaultValues
                    ? SetDefaultValues(defaultValues, input.register)
                    : undefined
                }
              />
            </Fragment>
          ))}
        </fieldset>
      ))}
      {error && <p>{error}</p>}
      <MainButton isLoading={isLoading}>{accountTexts.update}</MainButton>
    </form>
  );
};

export default AccountMembershipForm;
